/**
 * Formats a file size in bytes into a human-readable string with appropriate units.
 * Uses binary units (1024-based) but displays with common unit names (B, KB, MB, GB, etc.).
 *
 * @param bytes - The file size in bytes (must be a non-negative integer)
 * @returns A formatted string like "1.5KB", "10MB", "2.75GB"
 * @throws Error if bytes is negative or not a whole number
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 0) {
    throw new Error('File size cannot be negative')
  }

  if (!Number.isInteger(bytes)) {
    throw new Error('File size must be a whole number of bytes')
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let size = bytes
  let unitIndex = 0

  // Keep dividing by 1024 until we find the appropriate unit
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(size * 100) / 100
  return `${rounded}${units[unitIndex]}`
}
